'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Lock, Loader2, Eye, EyeOff, AlertTriangle, Clock } from 'lucide-react';

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [failureCount, setFailureCount] = useState(0);

  useEffect(() => {
    if (waitSeconds <= 0) return;
    
    const timer = setInterval(() => {
      setWaitSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [waitSeconds]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (waitSeconds > 0) return;
    
    if (!password.trim()) {
      setError('비밀번호를 입력해주세요.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch('/react/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        if (data.waitSeconds) {
          setWaitSeconds(data.waitSeconds);
        }
        if (data.failureCount) {
          setFailureCount(data.failureCount);
        }
        throw new Error(data.message || data.error || '인증에 실패했습니다.');
      }
      
      router.push(redirect);
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '인증 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [password, redirect, router, waitSeconds]);

  const isLocked = waitSeconds > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              isLocked ? 'bg-red-100' : 'bg-gray-100'
            }`}>
              {isLocked ? (
                <Clock className="w-8 h-8 text-red-600" />
              ) : (
                <Lock className="w-8 h-8 text-gray-600" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">접근 인증</h1>
            <p className="text-gray-500 mt-2">서비스 이용을 위해 비밀번호를 입력해주세요</p>
          </div>
          
          {isLocked && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium">일시적으로 잠김</p>
                  <p className="text-amber-700 text-sm mt-1">
                    보안을 위해 {waitSeconds}초 후에 다시 시도할 수 있습니다.
                  </p>
                  <div className="mt-3 bg-amber-100 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-amber-500 h-full transition-all duration-1000"
                      style={{ width: `${(waitSeconds / (waitSeconds + 1)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                비밀번호
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  disabled={isLocked}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  autoFocus={!isLocked}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLocked}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {error && !isLocked && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
                {failureCount >= 3 && (
                  <p className="text-xs mt-1 text-red-500">
                    {failureCount}회 실패 - 대기 시간이 증가합니다
                  </p>
                )}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || isLocked}
              className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors ${
                isLocked 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  인증 중...
                </>
              ) : isLocked ? (
                <>
                  <Clock className="w-5 h-5" />
                  {waitSeconds}초 대기 중...
                </>
              ) : (
                '접속하기'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
